import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Aviation-style emergency kill switch. Always visible.
 */
export default function KillSwitch({ active, onChange }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // ESC closes modal
  useEffect(() => {
    if (!confirming) return;
    const onKey = (e) => e.key === "Escape" && setConfirming(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirming]);

  const handleActivate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/risk/kill-switch/activate");
      onChange?.(r.data.kill_switch_active);
      toast.error(`Kill switch ACTIVE — ${r.data.cancelled_orders} pending orders cancelled.`);
      setConfirming(false);
    } catch (e) {
      toast.error("Failed to activate kill switch.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/risk/kill-switch/deactivate");
      onChange?.(r.data.kill_switch_active);
      toast.success("Kill switch released. Trading re-enabled.");
    } catch (e) {
      toast.error("Failed to deactivate.");
    } finally {
      setBusy(false);
    }
  };

  if (active) {
    return (
      <button
        type="button"
        className="kill-switch"
        data-active="true"
        data-testid="kill-switch-deactivate-btn"
        onClick={handleDeactivate}
        disabled={busy}
      >
        <span>● ACTIVE — RELEASE</span>
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className="kill-switch"
        data-testid="kill-switch-btn"
        onClick={() => setConfirming(true)}
      >
        <span className="font-mono">⚠</span>
        <span>EMERGENCY STOP</span>
      </button>
      {confirming && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="kill-switch-modal" style={{ background: "rgba(0,0,0,0.85)" }}>
          <div className="panel w-[440px] p-6 border-[#FF3B30]" style={{ borderColor: "var(--kill)" }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[#FF3B30] text-xl">⚠</span>
              <h2 className="text-lg font-semibold tracking-tight">Activate Emergency Stop?</h2>
            </div>
            <p className="text-sm text-[#8B95A5] leading-relaxed">
              All <span className="text-white">automated trading</span> will halt immediately. Pending orders
              will be cancelled. Open positions will <span className="text-white">remain</span> — you can close
              them manually.
            </p>
            <div className="text-xs text-[#FFB300] mt-3 font-mono">
              Mode will be reset to MANUAL. You must release the kill switch to re-enable AUTO.
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button
                className="btn-outline"
                onClick={() => setConfirming(false)}
                disabled={busy}
                data-testid="kill-switch-cancel-btn"
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleActivate}
                disabled={busy}
                data-testid="kill-switch-confirm-btn"
              >
                {busy ? "Activating..." : "Confirm — Stop Everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
